
import { useState } from 'react';
import { useCrypto } from '../../hooks/useCrypto';
import Header from '../../components/feature/Header';
import Card from '../../components/base/Card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';

const Analytics = () => {
  const { cryptoData, loading } = useCrypto();
  const [timeframe, setTimeframe] = useState<'1D' | '7D' | '1M' | '3M' | '1Y'>('7D');

  // Mock portfolio data
  const portfolioData = [
    { date: '2024-01-01', value: 45000 },
    { date: '2024-01-02', value: 47200 },
    { date: '2024-01-03', value: 46800 },
    { date: '2024-01-04', value: 49100 },
    { date: '2024-01-05', value: 51300 },
    { date: '2024-01-06', value: 50900 },
    { date: '2024-01-07', value: 52400 },
    { date: '2024-01-08', value: 54100 },
    { date: '2024-01-09', value: 53700 },
    { date: '2024-01-10', value: 55200 },
    { date: '2024-01-11', value: 57800 },
    { date: '2024-01-12', value: 56400 },
    { date: '2024-01-13', value: 58900 },
    { date: '2024-01-14', value: 61200 },
    { date: '2024-01-15', value: 63500 },
  ];

  // Mock allocation data
  const allocationData = [
    { name: 'Bitcoin', value: 45, amount: 28575, color: '#F7931A' },
    { name: 'Ethereum', value: 25, amount: 15875, color: '#627EEA' },
    { name: 'Binance Coin', value: 15, amount: 9525, color: '#F3BA2F' },
    { name: 'Cardano', value: 8, amount: 5080, color: '#0033AD' },
    { name: 'Solana', value: 7, amount: 4445, color: '#9945FF' },
  ];

  // Mock performance data
  const performanceData = [
    { asset: 'BTC', return7d: 12.5, return30d: 28.3, return1y: 145.2, allocation: 45 },
    { asset: 'ETH', return7d: 8.7, return30d: 22.1, return1y: 89.4, allocation: 25 },
    { asset: 'BNB', return7d: -2.3, return30d: 15.6, return1y: 67.8, allocation: 15 },
    { asset: 'ADA', return7d: 5.4, return30d: -8.2, return1y: 23.1, allocation: 8 },
    { asset: 'SOL', return7d: 15.8, return30d: 45.7, return1y: 234.5, allocation: 7 },
  ];

  // Mock trading volume data
  const volumeData = [
    { date: '01/09', volume: 12500 },
    { date: '01/10', volume: 15800 },
    { date: '01/11', volume: 9200 },
    { date: '01/12', volume: 18900 },
    { date: '01/13', volume: 22100 },
    { date: '01/14', volume: 16700 },
    { date: '01/15', volume: 24300 },
  ];

  const totalValue = 63500;
  const totalGain = 18500;
  const totalGainPercent = (totalGain / (totalValue - totalGain)) * 100;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading analytics data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Portfolio Analytics</h1>
          <p className="text-gray-600">Comprehensive analysis of your cryptocurrency investments</p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Total Value</h3>
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-money-dollar-circle-line text-2xl text-green-500"></i>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-3xl font-bold">${totalValue.toLocaleString()}</p>
              <p className="text-sm text-green-600 flex items-center">
                <i className="ri-arrow-up-line mr-1"></i>
                +${totalGain.toLocaleString()} ({totalGainPercent.toFixed(2)}%)
              </p>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">24h Change</h3>
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-line-chart-line text-2xl text-blue-500"></i>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-3xl font-bold text-green-600">+$2,340</p>
              <p className="text-sm text-green-600">+3.84%</p>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Best Performer</h3>
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-trophy-line text-2xl text-yellow-500"></i>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-2xl font-bold">SOL</p>
              <p className="text-sm text-green-600">+15.8% (7d)</p>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Total Assets</h3>
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-coins-line text-2xl text-purple-500"></i>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-3xl font-bold">5</p>
              <p className="text-sm text-gray-600">Active positions</p>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Portfolio Performance Chart */}
          <div className="lg:col-span-2">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold">Portfolio Performance</h3>
                <div className="flex bg-gray-100 rounded-lg p-1">
                  {['1D', '7D', '1M', '3M', '1Y'].map((period) => (
                    <button
                      key={period}
                      onClick={() => setTimeframe(period as any)}
                      className={`px-3 py-1 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                        timeframe === period
                          ? 'bg-blue-500 text-white'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      {period}
                    </button>
                  ))}
                </div>
              </div>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={portfolioData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    />
                    <YAxis 
                      tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                    />
                    <Tooltip 
                      formatter={(value: any) => [`$${value.toLocaleString()}`, 'Portfolio Value']}
                      labelFormatter={(label) => new Date(label).toLocaleDateString()}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="value" 
                      stroke="#3B82F6" 
                      strokeWidth={3}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          {/* Asset Allocation */}
          <Card className="p-6">
            <h3 className="text-xl font-semibold mb-6">Asset Allocation</h3>
            <div className="h-64 mb-4">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={allocationData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {allocationData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: any) => [`${value}%`, 'Allocation']} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-3">
              {allocationData.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: item.color }}
                    ></div>
                    <span className="text-sm font-medium">{item.name}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium">{item.value}%</p>
                    <p className="text-xs text-gray-500">${item.amount.toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Performance Table */}
          <Card className="p-6">
            <h3 className="text-xl font-semibold mb-6">Asset Performance</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3">Asset</th>
                    <th className="text-left py-3">7D</th>
                    <th className="text-left py-3">30D</th>
                    <th className="text-left py-3">1Y</th>
                    <th className="text-left py-3">Weight</th>
                  </tr>
                </thead>
                <tbody>
                  {performanceData.map((asset, index) => (
                    <tr key={index} className="border-b">
                      <td className="py-4">
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                            <span className="text-xs font-bold">{asset.asset}</span>
                          </div>
                          <span className="font-medium">{asset.asset}</span>
                        </div>
                      </td>
                      <td className={`py-4 ${asset.return7d >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {asset.return7d >= 0 ? '+' : ''}{asset.return7d.toFixed(1)}%
                      </td>
                      <td className={`py-4 ${asset.return30d >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {asset.return30d >= 0 ? '+' : ''}{asset.return30d.toFixed(1)}%
                      </td>
                      <td className={`py-4 ${asset.return1y >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {asset.return1y >= 0 ? '+' : ''}{asset.return1y.toFixed(1)}%
                      </td>
                      <td className="py-4">{asset.allocation}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Trading Volume */}
          <Card className="p-6">
            <h3 className="text-xl font-semibold mb-6">Trading Volume (7 Days)</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={volumeData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(value: any) => [`$${value.toLocaleString()}`, 'Volume']} />
                  <Bar dataKey="volume" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold">$119.5k</p>
                <p className="text-sm text-gray-600">Total Volume</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">+23.4%</p>
                <p className="text-sm text-gray-600">vs Last Week</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Risk Metrics */}
        <Card className="p-6 mt-6">
          <h3 className="text-xl font-semibold mb-6">Risk Analysis</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-shield-check-line text-2xl text-blue-600"></i>
              </div>
              <h4 className="font-semibold mb-1">Risk Score</h4>
              <p className="text-2xl font-bold text-blue-600">7.2</p>
              <p className="text-sm text-gray-600">Moderate Risk</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-line-chart-line text-2xl text-green-600"></i>
              </div>
              <h4 className="font-semibold mb-1">Sharpe Ratio</h4>
              <p className="text-2xl font-bold text-green-600">1.85</p>
              <p className="text-sm text-gray-600">Good Performance</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-alert-line text-2xl text-yellow-600"></i>
              </div>
              <h4 className="font-semibold mb-1">Volatility</h4>
              <p className="text-2xl font-bold text-yellow-600">24.3%</p>
              <p className="text-sm text-gray-600">30-Day Average</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-pie-chart-line text-2xl text-purple-600"></i>
              </div>
              <h4 className="font-semibold mb-1">Diversification</h4>
              <p className="text-2xl font-bold text-purple-600">8.5</p>
              <p className="text-sm text-gray-600">Well Diversified</p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Analytics;
